// =============================================================================
// DirectorHubScreen — /director  (T-395)
// The company log: James's director-locked sandbox for company-level updates
// and actions. Jot fast, categorise (company/finance/projects/compliance/
// people), assign + due-date actions, tick them off, attach docs — then roll
// the period up into a directors' meeting pack (PDF/Excel) with a live
// company snapshot. RLS is director-only (mig 231); the screen mirrors that.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Crown, Plus, AlertTriangle, CheckCircle2, CalendarClock, ClipboardList,
  FileText, Paperclip, MessageSquare, ChevronDown, ChevronRight, X,
  Download, Trash2, Building2, DollarSign, FolderKanban, ShieldCheck, Users,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import {
  exportDirectorReportPdf, exportDirectorReportXlsx,
  type ReportEntry, type CompanySnapshot,
} from "./directorReportExport";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

const BUCKET = "director-hub";

type Category = "company" | "finance" | "projects" | "compliance" | "people";
type EntryStatus = "open" | "in_progress" | "done" | "parked";

const CATEGORIES: { value: Category; label: string; icon: any }[] = [
  { value: "company",    label: "Company",    icon: Building2 },
  { value: "finance",    label: "Finance",    icon: DollarSign },
  { value: "projects",   label: "Projects",   icon: FolderKanban },
  { value: "compliance", label: "Compliance", icon: ShieldCheck },
  { value: "people",     label: "People",     icon: Users },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

const PRIORITIES = ["low", "normal", "high", "critical"] as const;

const STATUS_META: Record<EntryStatus, { label: string; tone: any }> = {
  open:        { label: "open",        tone: "warning" },
  in_progress: { label: "in progress", tone: "notice" },
  done:        { label: "done",        tone: "won" },
  parked:      { label: "parked",      tone: "default" },
};

const empName = (e: any) => e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : "";

const fmtShort = (v: number) =>
  isPresenting() ? MONEY_MASK
  : v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}m`
  : v >= 1_000   ? `$${Math.round(v / 1_000)}k`
  : `$${Math.round(v)}`;

// Live company snapshot — shared by the on-screen glance strip AND the
// weekly report. Same proven queries as the home/finance screens.
async function fetchCompanySnapshot(): Promise<CompanySnapshot> {
  const [proj, ar, ap, burn, comp] = await Promise.all([
    supabase.from("v_project_financials").select("contract_value, status").in("status", ["live", "mobilising", "acceptance_pending"]),
    supabase.from("client_invoices").select("total_incl_gst, amount_paid").in("status", ["sent", "part_paid", "overdue"]),
    supabase.from("ap_invoices").select("amount_inc_gst").in("status", ["approved", "entered_in_xero"]).eq("xero_entry_type", "bill"),
    supabase.from("v_company_monthly_burn").select("*").single(),
    supabase.from("v_compliance_calendar").select("urgency").in("urgency", ["expired", "overdue", "critical", "warning"]),
  ]);
  const n = (v: any) => Number(v ?? 0);
  return {
    liveProjects: proj.data?.length ?? 0,
    liveContractValue: (proj.data ?? []).reduce((s: number, r: any) => s + n(r.contract_value), 0),
    arOutstanding: (ar.data ?? []).reduce((s: number, r: any) => s + n(r.total_incl_gst) - n(r.amount_paid), 0),
    apOwing: (ap.data ?? []).reduce((s: number, r: any) => s + n(r.amount_inc_gst), 0),
    monthlyBurn: n((burn.data as any)?.total_monthly_burn_ex_gst),
    complianceExpiring30d: comp.data?.length ?? 0,
  };
}

export default function DirectorHubScreen() {
  const user = useRequireAuth();
  const queryClient = useQueryClient();
  const isDirector = hasRole(user, "director");

  const [filter, setFilter]       = useState<"open" | "mine" | "overdue" | "done" | "updates" | "all">("open");
  const [catFilter, setCatFilter] = useState<Category | "">("");
  const [expanded, setExpanded]   = useState<Set<string>>(new Set());
  const [modalEntry, setModalEntry] = useState<any | "new" | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ----- Data ----------------------------------------------------------------
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["director_company_log"],
    enabled: isDirector,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_log")
        .select(`*,
          assignee:employees!company_log_assigned_to_employee_id_fkey(first_name, last_name),
          project:projects(project_code),
          notes:company_log_notes(note_id, note, created_at, created_by),
          docs:company_log_documents(doc_id, storage_path, file_name)`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Live company glance — the "know what's going on" strip (refetches on focus)
  const { data: snapshot } = useQuery({
    queryKey: ["director_hub_snapshot"],
    enabled: isDirector,
    queryFn: fetchCompanySnapshot,
    staleTime: 60_000,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["director_hub_employees"],
    enabled: isDirector,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, is_active")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // ----- Mutations -----------------------------------------------------------
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["director_company_log"] });

  const statusMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: EntryStatus }) => {
      const patch: any = { status };
      if (status === "done") { patch.done_at = new Date().toISOString(); }
      else { patch.done_at = null; patch.done_by = null; }
      const { error } = await supabase.from("company_log").update(patch).eq("entry_id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("company_log").delete().eq("entry_id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  const noteMut = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      // created_by was never being set on any log write, so every entry and note
      // landed with a null author — the report can't attribute anything and
      // "mine" can't work (found 02/08 while chasing T-440).
      const { error } = await supabase.from("company_log_notes")
        .insert({ entry_id: id, note, created_by: user?.user_id ?? null });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  // ----- Derived -------------------------------------------------------------
  const today = new Date().toISOString().slice(0, 10);
  const myEmployeeId = user?.employee_id;

  const actions = useMemo(() => entries.filter(e => e.kind === "action"), [entries]);
  const openActions = useMemo(() => actions.filter(e => e.status === "open" || e.status === "in_progress"), [actions]);
  const overdueActions = useMemo(() => openActions.filter(e => e.due_date && e.due_date < today), [openActions, today]);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  const doneThisWeek = useMemo(() => actions.filter(e => e.status === "done" && e.done_at && e.done_at >= weekAgo), [actions, weekAgo]);
  const updatesThisWeek = useMemo(() => entries.filter(e => e.kind === "update" && e.created_at >= weekAgo), [entries, weekAgo]);

  const visible = useMemo(() => {
    let list = entries;
    if (catFilter) list = list.filter(e => e.category === catFilter);
    switch (filter) {
      case "open":    return list.filter(e => e.kind === "action" && (e.status === "open" || e.status === "in_progress" || e.status === "parked"));
      case "mine":    return list.filter(e => e.kind === "action" && e.assigned_to_employee_id === myEmployeeId && e.status !== "done");
      case "overdue": return list.filter(e => e.kind === "action" && e.status !== "done" && e.due_date && e.due_date < today);
      case "done":    return list.filter(e => e.kind === "action" && e.status === "done");
      case "updates": return list.filter(e => e.kind === "update");
      default:        return list;
    }
  }, [entries, filter, catFilter, myEmployeeId, today]);

  const openByCat = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of openActions) m[e.category] = (m[e.category] ?? 0) + 1;
    return m;
  }, [openActions]);

  // ----- Docs ----------------------------------------------------------------
  async function viewDoc(path: string) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) { setErr(error?.message ?? "Couldn't sign URL"); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  }

  // ----- Guards --------------------------------------------------------------
  if (!user) return null;
  if (!isDirector) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={Crown} title="Directors only" description="The Director hub is locked to director accounts." /></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-20">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Crown size={20} className="text-accent" /> Director hub
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Company log — jot updates and actions, assign them, tick them off, and roll the week up into the meeting pack.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={FileText} onClick={() => setReportOpen(true)}>Weekly report</Button>
          <Button variant="primary" icon={Plus} onClick={() => setModalEntry("new")}>New entry</Button>
        </div>
      </div>

      {err && (
        <Card className="mb-4 border-danger/30">
          <div className="p-3 flex items-center gap-2 text-sm text-danger">
            <AlertTriangle size={15} /> {err}
            <button className="ml-auto" onClick={() => setErr(null)}><X size={14} /></button>
          </div>
        </Card>
      )}

      {/* Company at a glance — live numbers, each tile jumps to its screen */}
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2 px-1">
          Company at a glance
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <GlanceTile to="/projects" label="Live projects"
            value={snapshot ? `${snapshot.liveProjects}` : "…"}
            sub={snapshot ? fmtShort(snapshot.liveContractValue) : ""} icon={FolderKanban} />
          <GlanceTile to="/invoices" label="AR outstanding"
            value={snapshot ? fmtShort(snapshot.arOutstanding) : "…"}
            sub="owed to us" icon={DollarSign} />
          <GlanceTile to="/ap/bills" label="AP owing"
            value={snapshot ? fmtShort(snapshot.apOwing) : "…"}
            sub="bills to pay" icon={ClipboardList} />
          <GlanceTile to="/opex" label="Monthly burn"
            value={snapshot ? fmtShort(snapshot.monthlyBurn) : "…"}
            sub="overheads ex GST" icon={Building2} />
          <GlanceTile to="/compliance" label="Compliance"
            value={snapshot ? `${snapshot.complianceExpiring30d}` : "…"}
            sub="need attention" icon={ShieldCheck}
            accent={(snapshot?.complianceExpiring30d ?? 0) > 0} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Open actions" value={openActions.length} icon={ClipboardList} />
        <KPI label="Overdue" value={overdueActions.length} icon={AlertTriangle} accent={overdueActions.length > 0} />
        <KPI label="Done this week" value={doneThisWeek.length} icon={CheckCircle2} />
        <KPI label="Updates this week" value={updatesThisWeek.length} icon={CalendarClock} />
      </div>

      {/* Quick jot — type it, Enter, done. Full modal only when you need
          assignee/due/docs. This is the "get it out of my head" path. */}
      <QuickJot onSaved={invalidate} onError={setErr} />


      {/* Category banners */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          const n = openByCat[c.value] ?? 0;
          const active = catFilter === c.value;
          return (
            <button key={c.value} onClick={() => setCatFilter(active ? "" : c.value)}
              className={`rounded-lg border p-3 text-left transition-colors ${
                active ? "border-accent/60 bg-accent/10" : "border-line bg-ink-card hover:border-accent/30"
              }`}>
              <div className="flex items-center gap-2">
                <Icon size={15} className="text-accent" />
                <span className="text-[13px] font-medium text-text">{c.label}</span>
              </div>
              <div className="text-[11.5px] text-text-muted mt-1">
                {n > 0 ? `${n} open action${n === 1 ? "" : "s"}` : "clear"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {([
          ["open", `Open (${openActions.length + actions.filter(a => a.status === "parked").length})`],
          ["mine", "Assigned to me"],
          ["overdue", `Overdue (${overdueActions.length})`],
          ["done", "Done"],
          ["updates", "Updates"],
          ["all", "Everything"],
        ] as const).map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
              filter === k ? "bg-accent/15 border-accent/40 text-accent font-medium" : "border-line text-text-muted hover:text-text"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Entries */}
      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : visible.length === 0 ? (
        <Card><EmptyState icon={ClipboardList} title="Nothing here"
          description={entries.length === 0 ? "Jot your first entry — an update the other directors should see, or an action someone should own." : "Nothing matches this filter."} /></Card>
      ) : (
        <div className="space-y-2">
          {visible.map(e => {
            const isAction = e.kind === "action";
            const overdue = isAction && e.status !== "done" && e.due_date && e.due_date < today;
            const isOpen = expanded.has(e.entry_id);
            return (
              <Card key={e.entry_id} className={`border-l-2 ${
                overdue ? "border-l-danger" : isAction && e.status === "done" ? "border-l-ok/60" : isAction ? "border-l-warn" : "border-l-accent/50"
              }`}>
                <div className="p-3.5">
                  <div className="flex items-start gap-3">
                    {/* Tick-off */}
                    {isAction && (
                      <button
                        onClick={() => statusMut.mutate({ id: e.entry_id, status: e.status === "done" ? "open" : "done" })}
                        disabled={statusMut.isPending}
                        className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                          e.status === "done" ? "bg-ok border-ok text-white" : "border-text-muted/50 hover:border-ok"
                        }`}
                        title={e.status === "done" ? "Re-open" : "Mark done"}
                      >
                        {e.status === "done" && <CheckCircle2 size={13} />}
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[14px] font-medium ${e.status === "done" && isAction ? "text-text-muted line-through" : "text-text"}`}>{e.title}</span>
                        <Pill label={CAT_LABEL[e.category] ?? e.category} small />
                        {isAction
                          ? <Pill label={STATUS_META[e.status as EntryStatus]?.label ?? e.status} tone={STATUS_META[e.status as EntryStatus]?.tone} small />
                          : <Pill label="update" tone="notice" small />}
                        {isAction && (e.priority === "high" || e.priority === "critical") && (
                          <Pill label={e.priority} tone="lost" small />
                        )}
                      </div>
                      <div className="text-[11.5px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                        {isAction && e.assignee && <span>→ {empName(e.assignee)}</span>}
                        {isAction && e.due_date && (
                          <span className={overdue ? "text-danger font-medium" : ""}>due {fmtDate(e.due_date)}{overdue ? " — overdue" : ""}</span>
                        )}
                        {e.project && <span>· {e.project.project_code}</span>}
                        <span>· logged {fmtDate(e.created_at)}</span>
                        {(e.notes?.length > 0 || e.docs?.length > 0) && (
                          <span className="inline-flex items-center gap-1.5">
                            {e.notes?.length > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare size={11} /> {e.notes.length}</span>}
                            {e.docs?.length > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip size={11} /> {e.docs.length}</span>}
                          </span>
                        )}
                      </div>
                      {e.details && !isOpen && <p className="text-[12px] text-text-muted mt-1 line-clamp-1">{e.details}</p>}
                    </div>
                    <button
                      onClick={() => setExpanded(prev => { const n = new Set(prev); n.has(e.entry_id) ? n.delete(e.entry_id) : n.add(e.entry_id); return n; })}
                      className="text-text-muted hover:text-text flex-shrink-0 mt-0.5"
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>

                  {isOpen && (
                    <ExpandedEntry
                      entry={e}
                      onStatus={(s) => statusMut.mutate({ id: e.entry_id, status: s })}
                      onDelete={() => { if (confirm("Delete this entry (and its notes/docs)?")) deleteMut.mutate(e.entry_id); }}
                      onEdit={() => setModalEntry(e)}
                      onNote={(note) => noteMut.mutate({ id: e.entry_id, note })}
                      onViewDoc={viewDoc}
                      onChanged={invalidate}
                      onError={setErr}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {modalEntry && (
        <EntryModal
          entry={modalEntry === "new" ? null : modalEntry}
          employees={employees}
          onClose={() => setModalEntry(null)}
          onSaved={() => { setModalEntry(null); invalidate(); }}
          onError={setErr}
        />
      )}

      {reportOpen && (
        <ReportModal entries={entries} onClose={() => setReportOpen(false)} onError={setErr} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GlanceTile — live company number that jumps to its screen
// ---------------------------------------------------------------------------
function GlanceTile({ to, label, value, sub, icon: Icon, accent }: {
  to: string; label: string; value: string; sub: string; icon: any; accent?: boolean;
}) {
  return (
    <Link to={to} className="block group">
      <div className={`rounded-lg border p-3 transition-colors h-full ${
        accent ? "border-warn/50 bg-warn/5" : "border-line bg-ink-card"
      } group-hover:border-accent/50`}>
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider2 text-text-muted font-semibold">
          <Icon size={12} className="text-accent" /> {label}
        </div>
        <div className={`text-[17px] font-semibold mt-1 ${accent ? "text-warn" : "text-text"}`}>{value}</div>
        <div className="text-[11px] text-text-muted">{sub}</div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// QuickJot — the friction-free capture path. One line, Enter, saved.
// Defaults: kind per toggle, category per chip, no assignee/due (add later
// via Edit on the card if it needs an owner).
// ---------------------------------------------------------------------------
function QuickJot({ onSaved, onError }: { onSaved: () => void; onError: (m: string) => void }) {
  const user = useRequireAuth();
  const [kind, setKind]         = useState<"action" | "update">("action");
  const [category, setCategory] = useState<Category>("company");
  const [text, setText]         = useState("");
  const [saving, setSaving]     = useState(false);

  async function save() {
    const title = text.trim();
    if (!title || saving) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("company_log")
        .insert({ kind, category, title, created_by: user?.user_id ?? null });
      if (error) throw error;
      setText("");
      onSaved();
    } catch (e: any) {
      onError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mb-4 border-accent/20">
      <div className="p-3 flex items-center gap-2 flex-wrap">
        <div className="flex rounded-full border border-line overflow-hidden flex-shrink-0">
          {(["action", "update"] as const).map(k => (
            <button key={k} onClick={() => setKind(k)}
              className={`px-3 py-1.5 text-[12px] ${kind === k ? "bg-accent/15 text-accent font-medium" : "text-text-muted hover:text-text"}`}>
              {k === "action" ? "Action" : "Update"}
            </button>
          ))}
        </div>
        <select value={category} onChange={e => setCategory(e.target.value as Category)}
          className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text flex-shrink-0">
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") save(); }}
          placeholder={kind === "action" ? "Jot an action… (Enter to save — assign/due later if needed)" : "Jot an update for the record… (Enter)"}
          className="flex-1 min-w-[220px] bg-ink border border-line rounded px-3 py-1.5 text-[13px] text-text placeholder:text-text-dim"
        />
        <Button size="sm" variant="primary" icon={Plus} onClick={save} disabled={!text.trim() || saving}>
          {saving ? "…" : "Jot"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Expanded entry — details, notes thread, docs, status + admin buttons
// ---------------------------------------------------------------------------
function ExpandedEntry({ entry, onStatus, onDelete, onEdit, onNote, onViewDoc, onChanged, onError }: {
  entry: any;
  onStatus: (s: EntryStatus) => void;
  onDelete: () => void;
  onEdit: () => void;
  onNote: (note: string) => void;
  onViewDoc: (path: string) => void;
  onChanged: () => void;
  onError: (m: string) => void;
}) {
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const isAction = entry.kind === "action";

  async function uploadDoc(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const safe = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `entries/${entry.entry_id}/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from(BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("company_log_documents")
        .insert({ entry_id: entry.entry_id, storage_path: path, file_name: file.name });
      if (insErr) throw insErr;
      onChanged();
    } catch (e: any) {
      onError(e?.message ?? String(e));
    } finally {
      setUploading(false);
    }
  }

  const sortedNotes = [...(entry.notes ?? [])].sort((a: any, b: any) => a.created_at.localeCompare(b.created_at));

  return (
    <div className="mt-3 pl-8 space-y-3">
      {entry.details && <p className="text-[12.5px] text-text whitespace-pre-wrap">{entry.details}</p>}

      {/* Docs */}
      <div className="flex items-center gap-2 flex-wrap">
        {(entry.docs ?? []).map((d: any) => (
          <button key={d.doc_id} onClick={() => onViewDoc(d.storage_path)}
            className="inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border border-line hover:border-accent text-text-muted hover:text-text">
            <Paperclip size={11} /> {d.file_name}
          </button>
        ))}
        <label className={`inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border border-dashed border-line cursor-pointer text-text-muted hover:border-accent hover:text-text ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <Paperclip size={11} /> {uploading ? "Uploading…" : "Attach doc"}
          <input type="file" className="hidden" onChange={uploadDoc} />
        </label>
      </div>

      {/* Notes thread */}
      {sortedNotes.length > 0 && (
        <div className="space-y-1.5">
          {sortedNotes.map((n: any) => (
            <div key={n.note_id} className="text-[12px] text-text-muted bg-ink-raised rounded px-2.5 py-1.5">
              {n.note} <span className="text-text-dim">· {fmtDate(n.created_at)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && note.trim()) { onNote(note.trim()); setNote(""); } }}
          placeholder="Add a progress note… (Enter)"
          className="flex-1 bg-ink border border-line rounded px-2.5 py-1.5 text-[12.5px] text-text placeholder:text-text-dim"
        />
      </div>

      {/* Status + admin row */}
      <div className="flex items-center gap-2 flex-wrap pt-1">
        {isAction && entry.status !== "in_progress" && entry.status !== "done" && (
          <Button size="sm" variant="secondary" onClick={() => onStatus("in_progress")}>Start</Button>
        )}
        {isAction && entry.status !== "parked" && entry.status !== "done" && (
          <Button size="sm" variant="ghost" onClick={() => onStatus("parked")}>Park</Button>
        )}
        <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
        <Button size="sm" variant="ghost" icon={Trash2} onClick={onDelete}>Delete</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New / edit entry modal
// ---------------------------------------------------------------------------
function EntryModal({ entry, employees, onClose, onSaved, onError }: {
  entry: any | null;
  employees: any[];
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [kind, setKind]         = useState<string>(entry?.kind ?? "action");
  const [category, setCategory] = useState<string>(entry?.category ?? "company");
  const [title, setTitle]       = useState(entry?.title ?? "");
  const [details, setDetails]   = useState(entry?.details ?? "");
  const [assignee, setAssignee] = useState<string>(entry?.assigned_to_employee_id ?? "");
  const [due, setDue]           = useState(entry?.due_date ?? "");
  const [priority, setPriority] = useState<string>(entry?.priority ?? "normal");
  const [saving, setSaving]     = useState(false);

  async function save() {
    if (!title.trim()) { onError("Give it a title."); return; }
    setSaving(true);
    try {
      const row: any = {
        kind, category,
        title: title.trim(),
        details: details.trim() || null,
        priority,
        assigned_to_employee_id: kind === "action" && assignee ? assignee : null,
        due_date: kind === "action" && due ? due : null,
      };
      if (entry) {
        const { error } = await supabase.from("company_log").update(row).eq("entry_id", entry.entry_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("company_log").insert(row);
        if (error) throw error;
      }
      onSaved();
    } catch (e: any) {
      onError(e?.message ?? String(e));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-line flex items-center justify-between">
          <h2 className="font-semibold text-[16px]">{entry ? "Edit entry" : "New entry"}</h2>
          <button onClick={onClose}><X size={18} className="text-text-muted hover:text-text" /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Kind toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setKind("action")}
              className={`rounded border px-3 py-2.5 text-left ${kind === "action" ? "border-accent/60 bg-accent/10" : "border-line"}`}>
              <div className="text-[13px] font-medium text-text">Action</div>
              <div className="text-[11px] text-text-muted">Assignable, due-dated, tick-offable</div>
            </button>
            <button onClick={() => setKind("update")}
              className={`rounded border px-3 py-2.5 text-left ${kind === "update" ? "border-accent/60 bg-accent/10" : "border-line"}`}>
              <div className="text-[13px] font-medium text-text">Update</div>
              <div className="text-[11px] text-text-muted">For the record — no owner needed</div>
            </button>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Category</label>
            <div className="flex gap-1.5 flex-wrap">
              {CATEGORIES.map(c => (
                <button key={c.value} onClick={() => setCategory(c.value)}
                  className={`px-3 py-1.5 rounded-full text-[12px] border ${category === c.value ? "bg-accent/15 border-accent/40 text-accent font-medium" : "border-line text-text-muted"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} autoFocus
              placeholder={kind === "action" ? "e.g. Renew WorkCover policy before 30 Aug" : "e.g. Switched insurance broker to XYZ"}
              className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text placeholder:text-text-dim" />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Details (optional)</label>
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3}
              className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text placeholder:text-text-dim" />
          </div>

          {kind === "action" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Assign to</label>
                <select value={assignee} onChange={e => setAssignee(e.target.value)}
                  className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text">
                  <option value="">— unassigned —</option>
                  {employees.map(e => (
                    <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Due date</label>
                <input type="date" value={due} onChange={e => setDue(e.target.value)}
                  className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Priority</label>
                <select value={priority} onChange={e => setPriority(e.target.value)}
                  className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text">
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : entry ? "Save changes" : "Add entry"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly report modal — period → snapshot fetch → PDF / Excel
// ---------------------------------------------------------------------------
function ReportModal({ entries, onClose, onError }: {
  entries: any[];
  onClose: () => void;
  onError: (m: string) => void;
}) {
  const weekAgoDate = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(weekAgoDate);
  const [end, setEnd]     = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy]   = useState<string | null>(null);

  function toReportEntry(e: any): ReportEntry {
    return {
      kind: e.kind, category: e.category, title: e.title, details: e.details,
      status: e.status, priority: e.priority,
      assignee: empName(e.assignee),
      due_date: e.due_date, done_at: e.done_at, created_at: e.created_at,
    };
  }

  const endIso = `${end}T23:59:59`;
  const newUpdates = entries.filter(e => e.kind === "update" && e.created_at >= start && e.created_at <= endIso).map(toReportEntry);
  const completed  = entries.filter(e => e.kind === "action" && e.status === "done" && e.done_at && e.done_at >= start && e.done_at <= endIso).map(toReportEntry);
  const openAct    = entries.filter(e => e.kind === "action" && e.status !== "done").map(toReportEntry);

  async function run(fmt: "pdf" | "xlsx") {
    setBusy(fmt);
    try {
      const snapshot = await fetchCompanySnapshot();
      const input = { periodStart: start, periodEnd: end, snapshot, newUpdates, completed, openActions: openAct };
      if (fmt === "pdf") await exportDirectorReportPdf(input);
      else exportDirectorReportXlsx(input);
    } catch (e: any) {
      onError(e?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-line">
          <h2 className="font-semibold text-[16px]">Directors' report</h2>
          <p className="text-[12px] text-text-muted mt-1">
            Meeting pack for the period — live company snapshot, updates, actions completed, and everything still open (overdue in red).
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">From</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)}
                className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">To</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)}
                className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text" />
            </div>
          </div>
          <div className="text-[12px] text-text-muted bg-ink-raised rounded px-3 py-2">
            {newUpdates.length} update{newUpdates.length === 1 ? "" : "s"} · {completed.length} completed · {openAct.length} still open
          </div>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={!!busy}>Close</Button>
          <Button variant="secondary" icon={Download} onClick={() => run("xlsx")} disabled={!!busy}>
            {busy === "xlsx" ? "Building…" : "Excel"}
          </Button>
          <Button variant="primary" icon={FileText} onClick={() => run("pdf")} disabled={!!busy}>
            {busy === "pdf" ? "Building…" : "PDF pack"}
          </Button>
        </div>
      </div>
    </div>
  );
}
